import React, { useEffect, useState, useCallback } from 'react';
import { useEnvironmentStore } from '../stores/environmentStore';
import { apiCall } from '../lib/api';
import { cn } from '../lib/utils';

interface ServiceHealth {
  reachable: boolean;
  status: number;
  data: any;
}

interface IntegrationSummary {
  poller: { health: ServiceHealth; stats: ServiceHealth };
  partner: { health: ServiceHealth; stats: ServiceHealth; providers: ServiceHealth };
}

interface Throughput {
  totals: { last_hour: string; last_day: string; last_week: string; total: string };
  hourly: Array<{ bucket: string; count: string }>;
}

export const DashboardPage: React.FC = () => {
  const { mode, novaHost } = useEnvironmentStore();
  const [summary, setSummary] = useState<IntegrationSummary | null>(null);
  const [throughput, setThroughput] = useState<Throughput | null>(null);
  const [recentEvents, setRecentEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, throughputRes, eventsRes] = await Promise.all([
        apiCall('/api/integration/summary').then(r => r.ok ? r.json() : null).catch(() => null),
        apiCall('/api/stats/throughput').then(r => r.ok ? r.json() : null).catch(() => null),
        apiCall('/api/events?limit=10').then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      setSummary(summaryRes);
      setThroughput(throughputRes);
      setRecentEvents(eventsRes?.events || []);
      setLastRefresh(new Date());
    } catch {
      // keep existing data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // ────────────────────────────────────────────────────────────────────
  // Derived insights
  // ────────────────────────────────────────────────────────────────────
  // poller /stats response shape: { poller: {...}, rabbitmq: {...}, events: {...} }
  const pollerStats = summary?.poller?.stats?.data?.poller;
  const pollerEvents = summary?.poller?.stats?.data?.events;
  const pollerRabbit = summary?.poller?.stats?.data?.rabbitmq;
  // partner /stats response shape: { consumer: {...}, rabbitmq: {...}, providers: {...}, recentEventsBy* }
  const partnerStats = summary?.partner?.stats?.data?.consumer;
  const partnerRabbit = summary?.partner?.stats?.data?.rabbitmq;
  const partnerByType = summary?.partner?.stats?.data?.recentEventsByType || {};
  const partnerByProvider = summary?.partner?.stats?.data?.recentEventsByProvider || {};
  // partner /providers response shape: { registry: { providerCount, providers: [...] }, stats: {...} }
  const providersList: any[] = summary?.partner?.providers?.data?.registry?.providers || [];
  const providerStats = summary?.partner?.providers?.data?.stats || {};

  const renderHealthCard = (name: string, health: ServiceHealth | undefined) => {
    const isUp = health?.reachable && health?.data?.status !== 'unhealthy';
    const statusData = health?.data;

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">{name}</h3>
          <span className={cn(
            'inline-block w-3 h-3 rounded-full',
            health === undefined ? 'bg-gray-300' :
            isUp ? 'bg-emerald-400' : 'bg-red-400'
          )} />
        </div>
        {health === undefined ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : !health.reachable ? (
          <p className="text-sm text-red-500">Unreachable</p>
        ) : (
          <div className="space-y-1 text-sm text-gray-600">
            <div>Status: <span className="font-medium">{statusData?.status || 'unknown'}</span></div>
            {statusData?.uptime != null && (
              <div>Uptime: <span className="font-medium">{formatUptime(statusData.uptime)}</span></div>
            )}
            {statusData?.pollCount != null && (
              <div>Polls: <span className="font-medium">{statusData.pollCount}</span></div>
            )}
            {statusData?.publishCount != null && (
              <div>Published: <span className="font-medium">{statusData.publishCount}</span></div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            {mode === 'production' ? 'Production' : 'Development'} &mdash; {novaHost || 'unknown host'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-gray-400">Updated {lastRefresh.toLocaleTimeString()}</span>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Throughput Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Last Hour</div>
          <div className="text-3xl font-bold text-gray-900">{throughput?.totals?.last_hour ?? '--'}</div>
          <div className="text-xs text-gray-500 mt-1">events</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Last 24h</div>
          <div className="text-3xl font-bold text-gray-900">{throughput?.totals?.last_day ?? '--'}</div>
          <div className="text-xs text-gray-500 mt-1">events</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Last 7 Days</div>
          <div className="text-3xl font-bold text-gray-900">{throughput?.totals?.last_week ?? '--'}</div>
          <div className="text-xs text-gray-500 mt-1">events</div>
        </div>
      </div>

      {/* Service Health */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Service Health</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {renderHealthCard('Nova Poller', summary?.poller?.health)}
          {renderHealthCard('PARTNER Integration', summary?.partner?.health)}
          {renderHealthCard('Ops UI', { reachable: true, status: 200, data: { status: 'ok', uptime: undefined } })}
        </div>
      </div>

      {/* Poller Details */}
      {pollerStats && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Poller Details</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <Stat label="Total Polls" value={pollerStats.pollCount} />
              <Stat label="Events Published" value={pollerStats.publishCount} />
              <Stat
                label="Last Poll"
                value={pollerStats.lastPollTime
                  ? new Date(pollerStats.lastPollTime).toLocaleTimeString()
                  : '—'}
              />
              <Stat
                label="RabbitMQ"
                value={pollerRabbit?.connected ? 'Connected' : 'Disconnected'}
                tone={pollerRabbit?.connected ? 'good' : 'bad'}
              />
            </div>
            {pollerStats.lastPollError && (
              <div className="mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                <span className="font-semibold">Last error:</span> {pollerStats.lastPollError}
              </div>
            )}
            {pollerEvents && (
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                {Object.entries(pollerEvents)
                  .filter(([k]) => k !== 'total' && typeof (pollerEvents as any)[k] !== 'object')
                  .slice(0, 4)
                  .map(([k, v]) => (
                    <Stat key={k} label={k.replace(/_/g, ' ')} value={String(v)} />
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Partner Integration Details */}
      {partnerStats && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">PARTNER Integration Details</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <Stat label="Processed" value={partnerStats.messagesProcessed} />
              <Stat
                label="Failed"
                value={partnerStats.messagesFailed}
                tone={partnerStats.messagesFailed > 0 ? 'bad' : 'neutral'}
              />
              <Stat label="Retried" value={partnerStats.messagesRetried} />
              <Stat
                label="DLQ"
                value={partnerStats.messagesSentToDlq}
                tone={partnerStats.messagesSentToDlq > 0 ? 'bad' : 'neutral'}
              />
              <Stat
                label="Success Rate"
                value={partnerStats.successRate}
                tone={parseFloat(String(partnerStats.successRate || '0')) >= 95 ? 'good' : 'neutral'}
              />
              <Stat label="Managed Queues" value={partnerStats.managedQueues ?? '—'} />
              <Stat
                label="Last Message"
                value={partnerStats.lastMessageTime
                  ? new Date(partnerStats.lastMessageTime).toLocaleTimeString()
                  : '—'}
              />
              <Stat
                label="RabbitMQ"
                value={partnerRabbit?.connected ? 'Connected' : 'Disconnected'}
                tone={partnerRabbit?.connected ? 'good' : 'bad'}
              />
            </div>
          </div>
        </div>
      )}

      {/* PARTNER Providers (live registry from partner-integration) */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          PARTNER Providers
          {providersList.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">({providersList.length})</span>
          )}
        </h2>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {providersList.length === 0 ? (
            <div className="px-5 py-3 text-sm text-gray-500">No provider data available</div>
          ) : (
            providersList.map((p: any, i: number) => {
              const ps = providerStats[p.id] || {};
              return (
                <div key={i} className="flex items-center gap-4 px-5 py-3 border-b border-gray-100 last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">{p.name || p.id}</span>
                      <span className="text-xs text-gray-400 font-mono">{p.id}</span>
                    </div>
                    {p.organizationIds && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        orgs: {p.organizationIds.join(', ')}
                      </div>
                    )}
                  </div>
                  {(ps.processed != null || ps.failed != null) && (
                    <div className="text-xs text-gray-500 hidden sm:block">
                      {ps.processed ?? 0} ok
                      {ps.failed ? <span className="text-red-500"> · {ps.failed} failed</span> : null}
                      {ps.successRate ? <span className="ml-2 text-gray-400">({ps.successRate})</span> : null}
                    </div>
                  )}
                  <span className={cn(
                    'text-xs font-medium px-2 py-0.5 rounded',
                    p.enabled !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                  )}>
                    {p.enabled !== false ? 'Active' : 'Disabled'}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Top Alert Types (last partner-integration window) */}
      {Object.keys(partnerByType).length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Top Alert Types (recent)</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="space-y-2">
              {topEntries(partnerByType, 8).map(([type, count]) => {
                const max = topEntries(partnerByType, 1)[0]?.[1] || 1;
                const pct = Math.max(2, (count / max) * 100);
                return (
                  <div key={type} className="flex items-center gap-3">
                    <span className="text-xs font-mono text-gray-700 w-48 truncate" title={type}>{type}</span>
                    <div className="flex-1 bg-gray-100 rounded h-2 overflow-hidden">
                      <div className="bg-primary/70 h-full rounded" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-medium text-gray-600 w-10 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
            {Object.keys(partnerByProvider).length > 1 && (
              <div className="mt-5 pt-4 border-t border-gray-100">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Recent deliveries by provider
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(partnerByProvider).map(([prov, count]) => (
                    <span key={prov} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-gray-100 rounded">
                      <span className="font-medium text-gray-700">{prov}</span>
                      <span className="text-gray-500">{String(count)}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hourly throughput chart (simple bar visualization) */}
      {throughput?.hourly && throughput.hourly.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Event Volume (Last 24h)</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-end gap-1 h-32">
              {(() => {
                const maxCount = Math.max(...throughput.hourly.map(h => parseInt(h.count) || 0), 1);
                return throughput.hourly.map((h, i) => {
                  const count = parseInt(h.count) || 0;
                  const height = Math.max((count / maxCount) * 100, 2);
                  const hour = new Date(h.bucket).getHours();
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${hour}:00 — ${count} events`}>
                      <div className="w-full bg-primary/70 rounded-t" style={{ height: `${height}%` }} />
                      {i % 4 === 0 && <span className="text-[10px] text-gray-400">{hour}h</span>}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Recent Events */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Events</h2>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {recentEvents.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No events recorded yet</div>
          ) : (
            recentEvents.slice(0, 10).map((evt, i) => {
              const payload = evt.event_payload;
              const severity = payload?.notification?.severity || 'info';
              const type = payload?.notification?.type || payload?.eventType || 'unknown';
              const message = payload?.notification?.message || '';
              const client = payload?.notification?.client || payload?.monitoringSession?.clipName || '';
              return (
                <div key={i} className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 last:border-0">
                  <span className={cn(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    severity === 'critical' || severity === 'high' ? 'bg-red-400' :
                    severity === 'warning' || severity === 'medium' ? 'bg-amber-400' :
                    severity === 'low' ? 'bg-blue-400' : 'bg-gray-300'
                  )} />
                  <span className="text-xs font-mono text-gray-400 w-16 flex-shrink-0">
                    {new Date(evt.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-sm font-medium text-gray-700 w-36 truncate flex-shrink-0">{type}</span>
                  {client && <span className="text-sm text-gray-500 truncate">{client}</span>}
                  {message && <span className="text-xs text-gray-400 truncate ml-auto max-w-xs">{message}</span>}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: any; tone?: 'good' | 'bad' | 'neutral' }> = ({ label, value, tone = 'neutral' }) => (
  <div>
    <div className="text-gray-400 text-xs uppercase tracking-wide">{label}</div>
    <div className={cn(
      'font-semibold',
      tone === 'good' ? 'text-emerald-600' :
      tone === 'bad' ? 'text-red-600' :
      'text-gray-900',
    )}>{value ?? '—'}</div>
  </div>
);

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function topEntries(obj: Record<string, number>, n: number): Array<[string, number]> {
  return Object.entries(obj)
    .map(([k, v]) => [k, Number(v) || 0] as [string, number])
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}
